// src/component/AppShell.jsx
import React, { useState, useEffect, createContext, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  Divider,
  BottomNavigation,
  BottomNavigationAction,
  Menu,
  MenuItem,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  Badge,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import { useLocation, useNavigate } from "react-router-dom";

import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import ColorLensRoundedIcon from "@mui/icons-material/ColorLensRounded";
import MilitaryTechRoundedIcon from "@mui/icons-material/MilitaryTechRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AutoStoriesRoundedIcon from "@mui/icons-material/AutoStoriesRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import ContactSupportRoundedIcon from "@mui/icons-material/ContactSupportRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { ListItemIcon } from "@mui/material";

import { useAuth } from "../context/auth";
import { db } from "../api/firebase";
import {
  doc,
  getDoc,
  updateDoc,
  onSnapshot,
  collection,
  query,
  orderBy,
  limit,
  where,
  getCountFromServer,
  Timestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import RequiredUserInfoDialog from "./RequiredUserInfoDialog";
import MM2024Dialog from "./MM2024Dialog";
import MM2025Dialog from "./MM2025Dialog";
import MM2026JoinDialog from "./MM2026JoinDialog";
import ribbon2024 from "../assets/MMM2024.png";
import ribbon2025 from "../assets/MMM2025.png";
import ribbon2026Color from "../assets/MMM2026.png";
import ribbon2026Gray from "../assets/MMM2026gray.png";

import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import RequiredCountryDialog from "./RequiredCountryDialog";
import RequiredJapaneseCityDialog from "./RequiredJapaneseCityDialog";
import AdminTitleBar, { TITLE_BAR_HEIGHT } from "./AdminTitleBar";
import { canSeeGroupActivity2026, is4kiPaidUser } from "./groupActivity2026Audience";
import {
  usePromoBanner,
  PromoBannerRailIcon,
  PromoBannerFloatingIcon,
  PromoBannerDialogMount,
} from "./PromoBannerLauncher";

export const AppThemeContext = createContext(null);
export const AppUserContext = createContext({ userInfo: null, isAdmin: false });

/* ==========================================================
    🎨 既存の 8種類の THEMES（和み〜桃花）
   ========================================================== */
const THEMES = [
  {
    name: "和み",
    reading: "なごみ",
    desc: "やわらかな紫と水色のグラデーション",
    appBg:
      "linear-gradient(135deg, rgba(228,236,255,0.55) 0%, rgba(255,249,255,0.55) 100%)",
    surfaceBg: "#FFFFFF",
    railBg: "linear-gradient(180deg, #F1ECFF 0%, #F6F7FF 60%, #FFFFFF 100%)",
    railItemBg: "rgba(255,255,255,0.96)",
    railItemActiveBg:
      "linear-gradient(180deg, rgba(125,92,255,0.18) 0%, rgba(0,183,255,0.14) 100%)",
    railText: "rgba(60,40,120,0.85)",
    railActive: "#5A35FF",
    bottomBg: "rgba(255,255,255,0.8)",
    bottomBorder: "rgba(0,0,0,0.1)",
    bottomSelected: "#5A35FF",
  },
  {
    name: "桜霞",
    reading: "さくらがすみ",
    desc: "ふんわり淡い桜色のトーン",
    appBg:
      "linear-gradient(135deg, rgba(255,213,230,0.55) 0%, rgba(255,249,252,0.6) 100%)",
    surfaceBg: "#FFFDFE",
    railBg: "linear-gradient(180deg, #FFE8F0 0%, #FFF4FA 60%, #FFFFFF 100%)",
    railItemBg: "rgba(255,255,255,0.98)",
    railItemActiveBg:
      "linear-gradient(180deg, rgba(255,168,201,0.22) 0%, rgba(255,233,244,0.9) 100%)",
    railText: "rgba(128,60,100,0.9)",
    railActive: "#E04588",
    bottomBg: "rgba(255,248,251,0.9)",
    bottomBorder: "rgba(224,160,190,0.5)",
    bottomSelected: "#E04588",
  },
  {
    name: "若葉",
    reading: "わかば",
    desc: "みずみずしい新緑のグラデーション",
    appBg:
      "linear-gradient(135deg, rgba(212,252,121,0.45) 0%, rgba(150,230,161,0.5) 100%)",
    surfaceBg: "#FBFFFB",
    railBg: "linear-gradient(180deg, #E9FFE9 0%, #F4FFF4 60%, #FFFFFF 100%)",
    railItemBg: "rgba(255,255,255,0.98)",
    railItemActiveBg:
      "linear-gradient(180deg, rgba(123,224,155,0.22) 0%, rgba(217,255,225,0.9) 100%)",
    railText: "rgba(35,91,60,0.9)",
    railActive: "#1E9E52",
    bottomBg: "rgba(244,255,247,0.9)",
    bottomBorder: "rgba(120,190,140,0.5)",
    bottomSelected: "#1E9E52",
  },
  {
    name: "夕映え",
    reading: "ゆうばえ",
    desc: "やわらかな夕焼け色のグラデーション",
    appBg:
      "linear-gradient(135deg, rgba(255,179,143,0.5) 0%, rgba(255,222,186,0.55) 100%)",
    surfaceBg: "#FFFCF8",
    railBg: "linear-gradient(180deg, #FFE5CE 0%, #FFF3E4 60%, #FFFFFF 100%)",
    railItemBg: "rgba(255,255,255,0.98)",
    railItemActiveBg:
      "linear-gradient(180deg, rgba(255,165,132,0.25) 0%, rgba(255,235,212,0.95) 100%)",
    railText: "rgba(115,72,42,0.9)",
    railActive: "#EB6A2A",
    bottomBg: "rgba(255,246,236,0.9)",
    bottomBorder: "rgba(220,160,120,0.45)",
    bottomSelected: "#EB6A2A",
  },
  {
    name: "水縹",
    reading: "みずはなだ",
    desc: "澄んだ水色のクリアなトーン",
    appBg:
      "linear-gradient(135deg, rgba(186,215,255,0.45) 0%, rgba(235,245,255,0.55) 100%)",
    surfaceBg: "#FFFFFF",
    railBg: "linear-gradient(180deg, #E6F1FF 0%, #F4F8FF 60%, #FFFFFF 100%)",
    railItemBg: "rgba(255,255,255,0.98)",
    railItemActiveBg:
      "linear-gradient(180deg, rgba(132,165,255,0.22) 0%, rgba(210,230,255,0.9) 100%)",
    railText: "rgba(60,80,130,0.9)",
    railActive: "#3F6EDB",
    bottomBg: "rgba(244,249,255,0.9)",
    bottomBorder: "rgba(150,170,215,0.45)",
    bottomSelected: "#3F6EDB",
  },
  {
    name: "山吹",
    reading: "やまぶき",
    desc: "やさしい黄金色のあたたかさ",
    appBg:
      "linear-gradient(135deg, rgba(255,236,170,0.50) 0%, rgba(255,248,220,0.55) 100%)",
    surfaceBg: "#FFFEFB",
    railBg: "linear-gradient(180deg, #FFF4CC 0%, #FFF9E4 60%, #FFFFFF 100%)",
    railItemBg: "rgba(255,255,255,0.98)",
    railItemActiveBg:
      "linear-gradient(180deg, rgba(255,212,128,0.25) 0%, rgba(255,240,200,0.95) 100%)",
    railText: "rgba(130,95,40,0.9)",
    railActive: "#E69A00",
    bottomBg: "rgba(255,251,235,0.9)",
    bottomBorder: "rgba(230,180,120,0.45)",
    bottomSelected: "#E69A00",
  },
  {
    name: "浅葱",
    reading: "あさぎ",
    desc: "静かな青緑の落ち着いたトーン",
    appBg:
      "linear-gradient(135deg, rgba(180,230,225,0.45) 0%, rgba(220,245,240,0.55) 100%)",
    surfaceBg: "#FFFFFF",
    railBg: "linear-gradient(180deg, #DFF7F4 0%, #F2FFFD 60%, #FFFFFF 100%)",
    railItemBg: "rgba(255,255,255,0.98)",
    railItemActiveBg:
      "linear-gradient(180deg, rgba(128,210,200,0.22) 0%, rgba(210,245,240,0.9) 100%)",
    railText: "rgba(40,95,90,0.9)",
    railActive: "#2C9E8E",
    bottomBg: "rgba(240,255,252,0.9)",
    bottomBorder: "rgba(120,190,175,0.45)",
    bottomSelected: "#2C9E8E",
  },
  {
    name: "桃花",
    reading: "ももか",
    desc: "ほのかな桃色のやわらかな空気感",
    appBg:
      "linear-gradient(135deg, rgba(255,225,235,0.45) 0%, rgba(255,245,250,0.55) 100%)",
    surfaceBg: "#FFFFFF",
    railBg: "linear-gradient(180deg, #FFE9F2 0%, #FFF4FA 60%, #FFFFFF 100%)",
    railItemBg: "rgba(255,255,255,0.98)",
    railItemActiveBg:
      "linear-gradient(180deg, rgba(255,180,200,0.20) 0%, rgba(255,235,245,0.90) 100%)",
    railText: "rgba(120,70,100,0.9)",
    railActive: "#D95286",
    bottomBg: "rgba(255,248,252,0.9)",
    bottomBorder: "rgba(220,170,190,0.45)",
    bottomSelected: "#D95286",
  },
];

const railWidth = 88;

/* ==========================================================
    🚩 userInfo に応じてメニューを生成する関数
   ========================================================== */
// 2026 グループへアクセス可能か：groupActivity2026.groupId 必須 + (admin or 4期支払い済み)
const canAccess2026Group = (userInfo, isAdmin) => {
  if (!userInfo?.groupActivity2026?.groupId) return false;
  if (isAdmin) return true;
  return is4kiPaidUser(userInfo);
};

const buildRailMenus = (userInfo, isAdmin) => {
  // 「グループ」ボタンは 2026 アクセス可否のみで判定（旧年度のカスケードは行わない）
  const hasAnyGroup = canAccess2026Group(userInfo, isAdmin);

  return [
    { key: "home", label: "ホーム", icon: <HomeRoundedIcon />, href: "/home" },
    {
      key: "groups",
      label: "グループ",
      icon: <GroupsRoundedIcon />,
      href: "/groups",
      disabled: !hasAnyGroup,
    },
    {
      key: "mypage",
      label: "マイページ",
      icon: <PersonRoundedIcon />,
      href: "/mypage",
    },
    {
      key: "community",
      label: "交流",
      icon: <ForumRoundedIcon />,
      href: "/communication",
    },
    {
      key: "admin",
      label: "管理者",
      icon: <AdminPanelSettingsRoundedIcon />,
      href: "/admin",
    },
  ];
};

// systemConfig のデフォルト（未作成でも動くように）
const DEFAULT_COMMUNITY_CFG = {
  enabled: true,
  allowAdminBypass: true,

  // 既存：期での解放
  maxPaymentPhase: 3,
  allowedPaymentPhases: [3],

  // ★ 追加：セカイムラ認証ユーザの解放
  allowSekaiMura: false,
};

// 2025-12-22 以降は「グループ導線を表示しない」
const isAfter20251222JST = () => {
  const now = new Date();
  const cutoff = new Date("2025-12-22T00:00:00+09:00");
  return now.getTime() >= cutoff.getTime();
};

/* ==========================================================
    ⭐ AppShell（メインコンポーネント）
   ========================================================== */
export default function AppShell({ outlet }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [themeIndex, setThemeIndex] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // リボンメニュー
  const [ribbonAnchorEl, setRibbonAnchorEl] = useState(null);
  const [open2024, setOpen2024] = useState(false);
  const [open2025, setOpen2025] = useState(false);
  const [open2026, setOpen2026] = useState(false);

  // リボン吹き出し
  const [ribbonBubble, setRibbonBubble] = useState({ enabled: false, text: "" });

  // 2026 グループ活動：マイページ動線ツールチップ
  //   audience: "admin" | "all" — 管理者限定／全ユーザー
  const [groupActivityTooltip, setGroupActivityTooltip] = useState({
    enabled: false,
    text: "",
    audience: "admin",
  });

  // 交流チャット種別選択ダイアログ
  const [chatSelectorOpen, setChatSelectorOpen] = useState(false);

  // ★ グループ選択ダイアログ
  const [groupSelectorOpen, setGroupSelectorOpen] = useState(false);

  // ★ 2026グループチャット 未読状態（グループアイコンの赤丸/数字用）
  const [groupChatLatestTs, setGroupChatLatestTs] = useState(0);
  const [groupChatLastReadTs, setGroupChatLastReadTs] = useState(0);
  const [groupChatUnreadCount, setGroupChatUnreadCount] = useState(0);

  // アカウントメニュー
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);

  // ★ ユーザーが持つ支払い期（payments[].paymentPhase）を number[] で保持
  const [userTerms, setUserTerms] = useState([]);
  const [loadingUserTerms, setLoadingUserTerms] = useState(true);

  // ★ systemConfig（交流チャット表示設定）
  const [communityCfg, setCommunityCfg] = useState(DEFAULT_COMMUNITY_CFG);
  const [loadingCommunityCfg, setLoadingCommunityCfg] = useState(true);

  // 交流権限なしダイアログ
  const [noCommunityOpen, setNoCommunityOpen] = useState(false);

  const theme = THEMES[themeIndex];

  const lastAppliedThemeRef = useRef(null); // "idx:3" or "name:和み" など

  // ──────────────────────
  // ダイアログ用ボタン共通スタイル
  // ──────────────────────
  const accent = theme.railActive || "#5A35FF";
  const surfaceChip = theme.railItemBg || "#FFFFFF";

  const dialogButtonBaseSx = {
    textTransform: "none",
    borderRadius: 999,
    fontWeight: 700,
    letterSpacing: "0.06em",
    py: 0.9,
  };

  const dialogOutlinedButtonSx = {
    ...dialogButtonBaseSx,
    borderColor: accent,
    color: accent,
    bgcolor: surfaceChip,
    "&:hover": {
      borderColor: accent,
      bgcolor: "rgba(0,0,0,0.03)",
    },
  };

  const dialogContainedButtonSx = {
    ...dialogButtonBaseSx,
    bgcolor: accent,
    color: "#fff",
    boxShadow: "0 10px 24px rgba(20,16,40,0.22)",
    "&:hover": {
      bgcolor: accent,
      boxShadow: "0 14px 28px rgba(20,16,40,0.28)",
      transform: "translateY(-1px)",
    },
  };

  const handleDrawerToggle = () => setMobileOpen((v) => !v);
  const openThemeMenu = (e) => setAnchorEl(e.currentTarget);
  const closeThemeMenu = () => setAnchorEl(null);

  const openRibbonMenu = (e) => setRibbonAnchorEl(e.currentTarget);
  const closeRibbonMenu = () => setRibbonAnchorEl(null);
  const handleRibbonSelect = (year) => {
    closeRibbonMenu();
    if (year === 2024) setOpen2024(true);
    if (year === 2025) setOpen2025(true);
    if (year === 2026) setOpen2026(true);
  };

  // ★ anchorEl を直接受け取る形にしておく
  const openAccountMenu = (anchor) => setAccountMenuAnchor(anchor);
  const closeAccountMenu = () => setAccountMenuAnchor(null);

  const handleSelectAccountMenu = async (action) => {
    closeAccountMenu();

    if (action === "profile") return navigate("/mypage");
    if (action === "contact") return navigate("/contactPage");
    if (action === "yaeChikai") return navigate("/yaeChikaiPage");
    if (action === "yaeChikaiBook") return navigate("/yaeChikaiBookPage");

    if (action === "logout") {
      try {
        const auth = getAuth();
        await auth.signOut();
        navigate("/");
      } catch (e) {
        console.error("Failed to sign out:", e);
      }
    }
  };

  // ─────────────────────────────
  // systemConfig/communityChatVisibility を購読
  // ─────────────────────────────
  useEffect(() => {
    const normalizeMax = (v) => {
      const n = Math.floor(Number(v));
      if (!Number.isFinite(n) || n < 1)
        return DEFAULT_COMMUNITY_CFG.maxPaymentPhase;
      return Math.min(n, 50);
    };

    const ref = doc(db, "systemConfig", "communityChatVisibility");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        const max = normalizeMax(
          data.maxPaymentPhase ?? DEFAULT_COMMUNITY_CFG.maxPaymentPhase
        );

        const allowed = Array.isArray(data.allowedPaymentPhases)
          ? data.allowedPaymentPhases
              .map((x) => Number(x))
              .filter((n) => Number.isFinite(n) && n >= 1 && n <= max)
              .sort((a, b) => a - b)
          : DEFAULT_COMMUNITY_CFG.allowedPaymentPhases;

        setCommunityCfg({
          enabled: data.enabled !== false,
          allowAdminBypass: data.allowAdminBypass !== false,
          maxPaymentPhase: max,
          allowedPaymentPhases: allowed || [],

          // ★ セカイムラ認証ユーザ解放（DBキーに合わせる）
          allowSekaiMura: data.allowSekaiMura === true,
        });
        setLoadingCommunityCfg(false);
      },
      (err) => {
        console.error("communityChatVisibility onSnapshot error:", err);
        // 取得失敗時は安全側へ（見せない）
        setCommunityCfg({
          enabled: false,
          allowAdminBypass: true,
          maxPaymentPhase: DEFAULT_COMMUNITY_CFG.maxPaymentPhase,
          allowedPaymentPhases: [],
          allowSekaiMura: false,
        });
        setLoadingCommunityCfg(false);
      }
    );

    return () => unsub();
  }, []);

  // リボン吹き出し設定を購読
  useEffect(() => {
    const ref = doc(db, "systemConfig", "ribbonBubble");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setRibbonBubble({
          enabled: data.enabled === true,
          text: data.text || "支払いはこちら",
          hideForPhases: Array.isArray(data.hideForPhases)
            ? data.hideForPhases.map(Number).filter(Number.isFinite)
            : [],
        });
      },
      (err) => {
        console.error("ribbonBubble onSnapshot error:", err);
      }
    );
    return () => unsub();
  }, []);

  // 2026 グループ活動ツールチップ設定を購読
  useEffect(() => {
    const ref = doc(db, "systemConfig", "groupActivity2026Dialog");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setGroupActivityTooltip({
          enabled: data.tooltipEnabled === true,
          text: data.tooltipText || "",
          audience: data.audience === "all" ? "all" : "admin",
        });
      },
      (err) => {
        console.error("groupActivity2026 tooltip onSnapshot error:", err);
      }
    );
    return () => unsub();
  }, []);

  // 2026グループチャットの最新メッセージ createdAt を購読（未読赤丸用）
  const myGroup2026Id = userInfo?.groupActivity2026?.groupId || null;
  useEffect(() => {
    if (!myGroup2026Id) {
      setGroupChatLatestTs(0);
      return undefined;
    }
    const qLatest = query(
      collection(db, "groupChats2026", myGroup2026Id, "messages"),
      orderBy("createdAt", "desc"),
      limit(1)
    );
    const unsub = onSnapshot(
      qLatest,
      (snap) => {
        const d = snap.docs[0];
        setGroupChatLatestTs(d?.data()?.createdAt?.toMillis?.() || 0);
      },
      (err) => console.error("group chat latest (shell) error:", err)
    );
    return () => unsub();
  }, [myGroup2026Id]);

  // 自分の最終既読 timestamp を購読（未読赤丸用）
  useEffect(() => {
    if (!myGroup2026Id || !user?.uid) {
      setGroupChatLastReadTs(0);
      return undefined;
    }
    const ref = doc(db, "users", user.uid, "groupChatReads", myGroup2026Id);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setGroupChatLastReadTs(
          snap.exists() ? snap.data()?.lastReadAt?.toMillis?.() || 0 : 0
        );
      },
      (err) => console.error("group chat lastRead (shell) error:", err)
    );
    return () => unsub();
  }, [myGroup2026Id, user?.uid]);

  const groupChatUnread =
    groupChatLatestTs > 0 && groupChatLatestTs > groupChatLastReadTs;

  // 未読件数を集計（getCountFromServer で軽くカウント）
  useEffect(() => {
    let cancelled = false;
    if (!myGroup2026Id || !groupChatUnread) {
      setGroupChatUnreadCount(0);
      return undefined;
    }
    (async () => {
      try {
        const col = collection(db, "groupChats2026", myGroup2026Id, "messages");
        const qUnread = groupChatLastReadTs
          ? query(
              col,
              where("createdAt", ">", Timestamp.fromMillis(groupChatLastReadTs))
            )
          : query(col);
        const snap = await getCountFromServer(qUnread);
        if (!cancelled) setGroupChatUnreadCount(snap.data().count || 0);
      } catch (err) {
        console.error("group chat unread count error:", err);
        // カウント失敗時は赤丸（ドット）にフォールバックできるよう 0 のまま
        if (!cancelled) setGroupChatUnreadCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [myGroup2026Id, groupChatUnread, groupChatLatestTs, groupChatLastReadTs]);

  /* ----------------------------------------------------------
      🔄 Firestore から userInfo + theme + isAdmin + payments をロード
     ---------------------------------------------------------- */
  useEffect(() => {
    if (!user) {
      setUserInfo(null);
      setIsAdmin(false);
      setUserTerms([]);
      setLoadingUserTerms(false);
      // ここで themeIndex を 0 に戻すかは好み。戻すなら setThemeIndex(0)
      return;
    }

    setLoadingUserTerms(true);

    const ref = doc(db, "users", user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setLoadingUserTerms(false);
          return;
        }

        const data = snap.data() || {};
        setUserInfo({ id: user.uid, ...data });
        setIsAdmin(!!data.isAdmin);

        // payments -> userTerms
        const payments = Array.isArray(data.payments) ? data.payments : [];
        const terms = payments
          .map((p) =>
            typeof p === "number" ? Number(p) : Number(p?.paymentPhase)
          )
          .filter((n) => Number.isFinite(n));
        setUserTerms(terms);

        // ✅ テーマ復元（onSnapshot で常に追従）
        // ただし「値が変わったときだけ反映」して、無駄な再描画や上書きを防ぐ
        const idxRaw = data.uiThemeIndex;
        const nameRaw = data.uiThemeName;

        let nextIdx = null;

        if (
          typeof idxRaw === "number" &&
          idxRaw >= 0 &&
          idxRaw < THEMES.length
        ) {
          nextIdx = idxRaw;
          const key = `idx:${nextIdx}`;
          if (lastAppliedThemeRef.current !== key) {
            lastAppliedThemeRef.current = key;
            setThemeIndex(nextIdx);
          }
        } else if (typeof nameRaw === "string" && nameRaw) {
          const idx = THEMES.findIndex((t) => t.name === nameRaw);
          if (idx >= 0) {
            nextIdx = idx;
            const key = `name:${nameRaw}`;
            if (lastAppliedThemeRef.current !== key) {
              lastAppliedThemeRef.current = key;
              setThemeIndex(idx);
            }
          }
        }

        setLoadingUserTerms(false);
      },
      (err) => {
        console.error("users/{uid} onSnapshot error:", err);
        setUserInfo(null);
        setIsAdmin(false);
        setUserTerms([]);
        setLoadingUserTerms(false);
      }
    );

    return () => unsub();
  }, [user, setThemeIndex]);

  // グループ選択ダイアログからの遷移
  const handleSelectGroup = (dest) => {
    if (!userInfo) return;

    if (dest === "2024" && userInfo.participatingGroup) {
      navigate(`/group/${userInfo.participatingGroup}`);
    }
    if (dest === "2025" && userInfo.groups2025) {
      navigate(`/groups2025/${userInfo.groups2025}`);
    }
    if (dest === "2025yae" && userInfo.groups2025SecondHalf) {
      navigate(`/groups2025hln/${userInfo.groups2025SecondHalf}`);
    }
    if (dest === "2026" && userInfo.groupActivity2026?.groupId) {
      navigate(`/groups2026/${userInfo.groupActivity2026.groupId}`);
    }

    setGroupSelectorOpen(false);
    setMobileOpen(false);
  };

  // 「グループ」ボタン押下時：ダイアログを介さず、2026 グループへ直接遷移
  // 2026 アクセス可否のみ判定（旧年度へのフォールバックは行わない）
  const resolveDirectGroupDest = () => {
    if (!canAccess2026Group(userInfo, isAdmin)) return null;
    return `/groups2026/${userInfo.groupActivity2026.groupId}`;
  };

  const handleGroupsDirectNav = () => {
    const dest = resolveDirectGroupDest();
    if (dest) {
      navigate(dest);
      setMobileOpen(false);
    }
  };

  /* ----------------------------------------------------------
      🎨 テーマ変更（Firestore 保存）
     ---------------------------------------------------------- */
  const handleChangeTheme = async (idx) => {
    setThemeIndex(idx);
    if (!user) return;

    try {
      const ref = doc(db, "users", user.uid);
      await updateDoc(ref, {
        uiThemeIndex: idx,
        uiThemeName: THEMES[idx].name,
      });
    } catch (err) {
      console.error("Failed to save theme:", err);
    }
  };

  /* ----------------------------------------------------------
      📌 メニュー構築＆管理者フィルタ
     ---------------------------------------------------------- */
  const railMenus = buildRailMenus(userInfo, isAdmin);

  const filteredMenus = railMenus.filter((m) => {
    if (m.key === "admin") return isAdmin;
    return true;
  });

  const activeKey =
    filteredMenus.find((m) => {
      return (
        location.pathname === m.href || location.pathname.startsWith(m.href)
      );
    })?.key ?? "home";

  /* ----------------------------------------------------------
      📩 「どのチャットを開きますか？」ダイアログからの遷移
     ---------------------------------------------------------- */
  const handleSelectChatMode = (mode) => {
    setChatSelectorOpen(false);
    navigate("/communication", { state: { chatMode: mode } });
    setMobileOpen(false);
  };

  /* ----------------------------------------------------------
      📩 「Direction」ボタンから /communityFeed へ遷移
     ---------------------------------------------------------- */
  const handleOpenDirection = () => {
    setChatSelectorOpen(false);
    navigate("/communityFeed");
    setMobileOpen(false);
  };

  /* ----------------------------------------------------------
      🧭 左レールからのメニュー選択
     ---------------------------------------------------------- */
  const handleNavSelect = (key, href, disabled) => {
    if (disabled) return;

    if (key === "community") {
      // ここが変更点：権限がなければ選択ダイアログを開かない
      // 読み込み中は「権限なし」ではなく selector を開いて待たせる
      if (loadingUserTerms || loadingCommunityCfg) {
        setChatSelectorOpen(true);
        return;
      }

      if (!canShowCommunityChats) {
        setNoCommunityOpen(true);
        return;
      }

      setChatSelectorOpen(true);
      return;
    }

    if (key === "groups") {
      handleGroupsDirectNav();
    } else {
      navigate(href);
      setMobileOpen(false);
    }
  };

  // リボン吹き出しの実効表示（hideForPhases に該当する期を支払い済みなら非表示）
  const showRibbonBubble = ribbonBubble.enabled &&
    !(ribbonBubble.hideForPhases || []).some((phase) =>
      (userTerms || []).some((t) => Number(t) === phase)
    );

  // 上部プッシュ通知（左レール／モバイルフローティング）
  const promo = usePromoBanner();

  /* ----------------------------------------------------------
      🧭 ナビゲーションレール（左）
     ---------------------------------------------------------- */
  // ツールチップの実効表示：公開範囲 admin → 管理者のみ、all → 4期支払い済み（admin は常に可）
  const groupActivityTooltipEffective = {
    ...groupActivityTooltip,
    enabled:
      groupActivityTooltip.enabled &&
      !!groupActivityTooltip.text &&
      canSeeGroupActivity2026({
        audience: groupActivityTooltip.audience,
        isAdmin,
        userData: userInfo,
      }),
  };

  const promoRailIcon = promo.shouldShow ? (
    <PromoBannerRailIcon
      tooltipText={promo.tooltipText}
      onClick={promo.openDialog}
      theme={theme}
    />
  ) : null;

  const railMenusWithBadge = filteredMenus.map((m) => ({
    ...m,
    badge: m.key === "groups" && groupChatUnread,
    badgeCount: m.key === "groups" ? groupChatUnreadCount : 0,
  }));

  const rail = (
    <NavRail
      menus={railMenusWithBadge}
      activeKey={activeKey}
      onSelect={handleNavSelect}
      onOpenThemeMenu={openThemeMenu}
      onOpenRibbonMenu={openRibbonMenu}
      onOpenAccountMenu={openAccountMenu}
      avatarUrl={userInfo?.avatar || user?.photoURL || ""}
      theme={theme}
      ribbonBubble={{ ...ribbonBubble, enabled: showRibbonBubble }}
      groupActivityTooltip={groupActivityTooltipEffective}
      promoIcon={promoRailIcon}
    />
  );

  // ★ 交流チャット表示可否（設定 × ユーザーの期 × セカイムラ × 管理者バイパス）
  const canShowCommunityChats = (() => {
    if (loadingUserTerms || loadingCommunityCfg) return false;

    // 運営停止
    if (!communityCfg.enabled) return false;

    // 管理者バイパス
    if (communityCfg.allowAdminBypass && isAdmin) return true;

    // ★ セカイムラ認証で解放（Admin側でONのときのみ）
    if (communityCfg.allowSekaiMura && userInfo?.isSekaimura === true)
      return true;

    // 期で解放
    const allowed = new Set(
      (communityCfg.allowedPaymentPhases || []).map((n) => Number(n))
    );
    return (userTerms || []).some((t) => allowed.has(Number(t)));
  })();

  const noCommunityTitle = !communityCfg.enabled
    ? "現在表示できません。"
    : "参照権限がありません。";

  const noCommunityBody = !communityCfg.enabled
    ? "現在、交流チャットは一時的に利用できません。時間をおいてお試しください。"
    : "このチャットは、お支払い完了後に閲覧できるようになります。";

  // ★ 3期の支払いがあるか（payments[].paymentPhase に 3 が含まれるか）
  const hasPhase3 = (() => {
    if (loadingUserTerms) return false;
    return (userTerms || []).some((t) => Number(t) === 3);
  })();

  // ★ 4期の支払いがあるか（payments[].paymentPhase に 4 が含まれるか）
  const hasPhase4 = (() => {
    if (loadingUserTerms) return false;
    return (userTerms || []).some((t) => Number(t) === 4);
  })();

  // --- build/version 表示用（users/{uid} から）
  const buildId = userInfo?.buildId;
  const buildCode = userInfo?.buildCode;

  const versionLabel =
    typeof buildId === "string" && buildId.trim()
      ? buildId.trim()
      : Number.isFinite(Number(buildCode))
      ? String(buildCode)
      : "";

  /* ----------------------------------------------------------
      🧱 レイアウト本体
     ---------------------------------------------------------- */
  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100dvh",
        position: "relative",
        background: theme.appBg,
        backgroundSize: "200% 200%",
        animation: "waveMove 26s ease-in-out infinite",
        "@keyframes waveMove": {
          "0%": { backgroundPosition: "0% 50%" },
          "50%": { backgroundPosition: "100% 50%" },
          "100%": { backgroundPosition: "0% 50%" },
        },
      }}
    >
      <RequiredUserInfoDialog />
      {/* <RequiredCountryDialog /> */}
      <RequiredJapaneseCityDialog />

      {/* タイトルロゴ: 全幅 fixed でレール+メインにまたがる */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 1100,
          pointerEvents: "none",
        }}
      >
        <Box sx={{ pointerEvents: "auto" }}>
          <AdminTitleBar />
        </Box>
      </Box>

      {/* 左レール（PC） */}
      <Box component="nav" aria-label="sidebar navigation">
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: railWidth,
              border: "none",
              boxSizing: "border-box",
              background: theme.railBg,
            },
          }}
        >
          {rail}
        </Drawer>
      </Box>

      {/* メインコンテンツ */}
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { md: `calc(100% - ${railWidth}px)` },
          ml: { md: `${railWidth}px` },
          p: { xs: 0, md: 2 },
          overflowX: "hidden",
        }}
      >
        <Box
          sx={{
            borderRadius: { xs: 0, md: 6 },
            overflow: "hidden",
            bgcolor: theme.surfaceBg,
            minHeight: "100dvh",
            boxShadow: { md: "0 20px 60px rgba(20,16,40,0.08)" },
            transition: "background 0.3s ease",
            pt: `${TITLE_BAR_HEIGHT}px`,
          }}
        >
          <AppThemeContext.Provider value={theme}>
            <AppUserContext.Provider value={{ userInfo, isAdmin }}>
              <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
                {React.cloneElement(outlet, { theme })}
              </Box>
            </AppUserContext.Provider>
          </AppThemeContext.Provider>
        </Box>
      </Box>

      {/* モバイル下ナビ */}
      <BottomNavigation
        value={activeKey}
        onChange={(_e, newValue) => {
          const menu = filteredMenus.find((m) => m.key === newValue);
          if (!menu || menu.disabled) return;

          if (menu.key === "community") {
            if (loadingUserTerms || loadingCommunityCfg) {
              setNoCommunityOpen(true);
              return;
            }
            if (!canShowCommunityChats) {
              setNoCommunityOpen(true);
              return;
            }
            setChatSelectorOpen(true);
          } else if (menu.key === "groups") {
            handleGroupsDirectNav();
          } else {
            navigate(menu.href);
          }
        }}
        showLabels
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          display: { xs: "flex", md: "none" },
          backdropFilter: "blur(14px)",
          background: theme.bottomBg,
          borderTop: `1px solid ${theme.bottomBorder}`,
          zIndex: 1200,
          "& .MuiBottomNavigationAction-root": {
            minWidth: 0,
            flex: 1,
            py: 0.25,
            px: 0,
            color: theme.railText,
          },
          "& .Mui-selected": {
            color: theme.bottomSelected + " !important",
          },
        }}
      >
        {filteredMenus.map((m) => {
          const isGroupsUnread = m.key === "groups" && groupChatUnread;
          const isMypageDot =
            m.key === "mypage" && groupActivityTooltipEffective.enabled;
          let iconNode = m.icon;
          if (isGroupsUnread) {
            iconNode = (
              <Badge
                color="error"
                overlap="circular"
                max={99}
                badgeContent={groupChatUnreadCount || undefined}
                variant={groupChatUnreadCount > 0 ? "standard" : "dot"}
                sx={{
                  "& .MuiBadge-badge": {
                    boxShadow: "0 0 0 2px #fff",
                    fontSize: 10,
                    height: 16,
                    minWidth: 16,
                    px: 0.4,
                  },
                }}
              >
                {m.icon}
              </Badge>
            );
          } else if (isMypageDot) {
            iconNode = (
              <Badge
                color="error"
                variant="dot"
                overlap="circular"
                sx={{
                  "& .MuiBadge-badge": {
                    boxShadow: "0 0 0 2px #fff",
                  },
                }}
              >
                {m.icon}
              </Badge>
            );
          }
          return (
            <BottomNavigationAction
              key={m.key}
              label={m.label}
              value={m.key}
              icon={iconNode}
            />
          );
        })}

        {/* アカウント用アクション（右端） */}
        <BottomNavigationAction
          key="account"
          value="account"
          label=""
          icon={
            <Avatar
              src={userInfo?.avatar || user?.photoURL || ""}
              sx={{ width: 24, height: 24 }}
            />
          }
          onClick={(e) => openAccountMenu(e.currentTarget)}
        />
      </BottomNavigation>

      {/* モバイル：マイページ上の 2026 グループ ツールチップ */}
      {(() => {
        if (!groupActivityTooltipEffective.enabled) return null;
        const mypageIndex = filteredMenus.findIndex((m) => m.key === "mypage");
        if (mypageIndex < 0) return null;
        const totalSlots = filteredMenus.length + 1; // ＋アカウント
        const centerPct = ((mypageIndex + 0.5) / totalSlots) * 100;
        return (
          <Box
            onClick={() => navigate("/mypage")}
            sx={{
              position: "fixed",
              // ボトムナビの直上（ナビ高さ ~56px + 矢印分の余白）
              bottom: 64,
              left: `${centerPct}%`,
              transform: "translateX(-50%)",
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              alignItems: "center",
              zIndex: 1300,
              cursor: "pointer",
              pointerEvents: "auto",
              // 軽い揺れアニメーションで注意喚起
              animation: "ga2026Bob 2.2s ease-in-out infinite",
              "@keyframes ga2026Bob": {
                "0%, 100%": { transform: "translateX(-50%) translateY(0)" },
                "50%": { transform: "translateX(-50%) translateY(-3px)" },
              },
            }}
          >
            {/* 吹き出し本体 */}
            <Box
              sx={{
                position: "relative",
                bgcolor: theme.railActive,
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1.3,
                px: 1.2,
                py: 0.6,
                borderRadius: 2,
                boxShadow: "0 10px 24px rgba(0,0,0,0.25)",
                whiteSpace: "nowrap",
                maxWidth: "62vw",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {groupActivityTooltip.text}
            </Box>
            {/* 矢印（吹き出しからアイコンへ向けて伸びる） */}
            <Box
              sx={{
                width: 0,
                height: 0,
                borderLeft: "8px solid transparent",
                borderRight: "8px solid transparent",
                borderTop: `10px solid ${theme.railActive}`,
                filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.15))",
              }}
            />
          </Box>
        );
      })()}
      {/* モバイル：上部プッシュ通知アイコン（リボンボタンの左） */}
      {promo.shouldShow && (
        <Box
          sx={{
            position: "fixed",
            right: 112,
            bottom: 64,
            display: { xs: "flex", md: "none" },
            zIndex: 1100,
          }}
        >
          <PromoBannerFloatingIcon
            tooltipText={promo.tooltipText}
            onClick={promo.openDialog}
            theme={theme}
          />
        </Box>
      )}

      {/* モバイルリボンボタン（テーマボタンの左） */}
      <Box sx={{ position: "fixed", right: 64, bottom: 64, display: { xs: "flex", md: "none" }, zIndex: 1100 }}>
        {/* 吹き出し */}
        {showRibbonBubble && (
          <Box
            sx={{
              position: "absolute",
              right: 48,
              top: "50%",
              transform: "translateY(-50%)",
              whiteSpace: "nowrap",
              bgcolor: theme.railActive,
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              px: 1.5,
              py: 0.7,
              borderRadius: "12px 12px 4px 12px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
              "&::after": {
                content: '""',
                position: "absolute",
                right: -6,
                top: "50%",
                transform: "translateY(-50%)",
                width: 0,
                height: 0,
                borderTop: "6px solid transparent",
                borderBottom: "6px solid transparent",
                borderLeft: `6px solid ${theme.railActive}`,
              },
            }}
          >
            {ribbonBubble.text}
          </Box>
        )}
        <IconButton
          onClick={openRibbonMenu}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: theme.bottomBg,
            border: `1px solid ${theme.bottomBorder}`,
            boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
          }}
        >
          <MilitaryTechRoundedIcon sx={{ fontSize: 22, color: theme.railActive }} />
        </IconButton>
      </Box>

      {/* モバイルテーマボタン */}
      <IconButton
        onClick={openThemeMenu}
        sx={{
          position: "fixed",
          right: 16,
          bottom: 64,
          display: { xs: "flex", md: "none" },
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: theme.bottomBg,
          border: `1px solid ${theme.bottomBorder}`,
          boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
          zIndex: 1100,
        }}
      >
        <ColorLensRoundedIcon sx={{ fontSize: 22, color: theme.railActive }} />
      </IconButton>

      {/* テーマ選択メニュー */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeThemeMenu}
        MenuListProps={{ dense: true }}
        PaperProps={{
          sx: {
            width: 160,
            borderRadius: 3,
            boxShadow: "0 10px 30px rgba(15,10,40,0.18)",
          },
        }}
      >
        <Box sx={{ px: 1.2, py: 1 }}>
          {THEMES.map((t, i) => {
            const selected = i === themeIndex;
            return (
              <MenuItem
                key={t.name}
                onClick={() => handleChangeTheme(i)}
                sx={{ px: 1.2, py: 0.9, mb: 0.3, borderRadius: 2 }}
              >
                <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 26,
                      borderRadius: 1.5,
                      background: t.appBg,
                      backgroundSize: "160% 160%",
                      boxShadow: "0 3px 8px rgba(0,0,0,0.06)",
                      border: selected
                        ? "2px solid rgba(90,53,255,0.85)"
                        : "1px solid rgba(255,255,255,0.9)",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      flexGrow: 1,
                      fontWeight: selected ? 700 : 600,
                      color: t.railActive,
                      opacity: 0.95,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t.name}
                  </Typography>
                  {selected && (
                    <CheckCircleRoundedIcon
                      sx={{ fontSize: 18, color: t.railActive }}
                    />
                  )}
                </Stack>
              </MenuItem>
            );
          })}
        </Box>
      </Menu>

      {/* リボンメニュー */}
      <Menu
        anchorEl={ribbonAnchorEl}
        open={Boolean(ribbonAnchorEl)}
        onClose={closeRibbonMenu}
        MenuListProps={{ dense: true }}
        PaperProps={{
          sx: {
            width: 180,
            borderRadius: 3,
            boxShadow: "0 10px 30px rgba(15,10,40,0.18)",
          },
        }}
      >
        {[
          { year: 2024, src: ribbon2024 },
          { year: 2025, src: ribbon2025 },
          { year: 2026, src: hasPhase4 ? ribbon2026Color : ribbon2026Gray },
        ].map((item) => (
          <MenuItem
            key={item.year}
            onClick={() => handleRibbonSelect(item.year)}
            sx={{ px: 1, py: 0.8, borderRadius: 2, mx: 0.5, mb: 0.3, justifyContent: "center" }}
          >
            <Box
              component="img"
              src={item.src}
              alt={String(item.year)}
              sx={{ height: 32, width: "auto" }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* リボンダイアログ */}
      <MM2024Dialog open={open2024} onClose={() => setOpen2024(false)} />
      <MM2025Dialog open={open2025} onClose={() => setOpen2025(false)} />
      <MM2026JoinDialog open={open2026} onClose={() => setOpen2026(false)} />

      {/* 上部プッシュ通知ダイアログ */}
      <PromoBannerDialogMount promo={promo} />

      {/* アカウントメニュー */}
      <Menu
        anchorEl={accountMenuAnchor}
        open={Boolean(accountMenuAnchor)}
        onClose={closeAccountMenu}
        MenuListProps={{ dense: true }}
        PaperProps={{
          sx: {
            width: 200,
            borderRadius: 3,
            boxShadow: "0 10px 30px rgba(15,10,40,0.2)",
          },
        }}
      >
        <MenuItem onClick={() => window.location.reload()}>
          <ListItemIcon>
            <RefreshRoundedIcon fontSize="small" />
          </ListItemIcon>
          再読み込み
        </MenuItem>

        {/* 3期のみ表示 */}
        {/* {hasPhase3 && (
          <MenuItem onClick={() => handleSelectAccountMenu("yaeChikai")}>
            <ListItemIcon>
              <AutoStoriesRoundedIcon fontSize="small" />
            </ListItemIcon>
            八重の誓い
          </MenuItem>
        )} */}

        {/* 3期のみ表示 */}
        {/* {hasPhase3 && (
          <MenuItem onClick={() => handleSelectAccountMenu("yaeChikaiBook")}>
            <ListItemIcon>
              <MenuBookRoundedIcon fontSize="small" />
            </ListItemIcon>
            八重の誓いブック
          </MenuItem>
        )} */}

        {/* 4期のみ表示 */}
        {hasPhase4 && (
          <MenuItem onClick={() => handleSelectAccountMenu("contact")}>
            <ListItemIcon>
              <ContactSupportRoundedIcon fontSize="small" />
            </ListItemIcon>
            お問い合わせ
          </MenuItem>
        )}

        <MenuItem onClick={() => handleSelectAccountMenu("logout")}>
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
          ログアウト
        </MenuItem>

        {/* version 表示（クリック不可・薄いグレー） */}
        {!!versionLabel && (
          <Box
            sx={{
              px: 2,
              pt: 0.6,
              pb: 1.1,
              opacity: 0.55,
              userSelect: "none",
            }}
          >
            <Typography variant="caption" sx={{ display: "block" }}>
              version: {versionLabel}
            </Typography>
          </Box>
        )}
      </Menu>

      {/* 交流チャット種別の選択ダイアログ */}
      <Dialog
        open={chatSelectorOpen}
        onClose={() => setChatSelectorOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 0 }}>
          <DialogSectionHeader
            icon={<ForumRoundedIcon />}
            label="COMMUNITY"
            subtitle="つながる場所を選んでください"
            theme={theme}
          />
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1, mb: 1 }}>
            {loadingUserTerms || loadingCommunityCfg ? (
              <Typography variant="body2" sx={{ opacity: 0.7, px: 0.5 }}>
                読み込み中...
              </Typography>
            ) : canShowCommunityChats ? (
              <>
                <Button
                  variant="outlined"
                  onClick={() => handleSelectChatMode("prefecture")}
                  sx={dialogOutlinedButtonSx}
                >
                  県Chat
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => handleSelectChatMode("region")}
                  sx={dialogOutlinedButtonSx}
                >
                  地域Chat
                </Button>

                {/* 全国Chat を戻す場合はここ */}
              </>
            ) : (
              <Box sx={{ px: 0.5, py: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {!communityCfg.enabled
                    ? "現在表示できません。"
                    : "参照権限がありません。"}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.8,
                    opacity: 0.78,
                    lineHeight: 1.6,
                  }}
                >
                  {!communityCfg.enabled ? (
                    <>
                      現在、交流チャットは一時的に利用できません。
                      <br />
                      時間をおいてお試しください。
                    </>
                  ) : (
                    <>
                      このチャットは、お支払い完了後または認証完了後に閲覧できるようになります。
                      <br />
                      対象の方は、運営の案内に沿ってお進みください。
                    </>
                  )}
                </Typography>

                {/* 強制停止の時は「お支払いページへ」を出さない */}
                {communityCfg.enabled && (
                  <Button
                    variant="contained"
                    onClick={() => {
                      setChatSelectorOpen(false);
                      navigate("/mm2026join-content");
                      setMobileOpen(false);
                    }}
                    sx={{ ...dialogContainedButtonSx, mt: 1.3, width: "100%" }}
                  >
                    お支払いページへ
                  </Button>
                )}
              </Box>
            )}

            {/* Direction ボタン — 一時非表示 */}
          </Stack>
        </DialogContent>
      </Dialog>

      {/* グループ選択ダイアログ */}
      <Dialog
        open={groupSelectorOpen}
        onClose={() => setGroupSelectorOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 0 }}>
          <DialogSectionHeader
            icon={<GroupsRoundedIcon />}
            label="GROUPS"
            subtitle="参加中のグループから選択"
            theme={theme}
          />
        </DialogTitle>

        <DialogContent>
          {isAfter20251222JST() ? (
            <Box sx={{ mt: 1, mb: 1, px: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                マスターマインドメンバーズ2026のグループ活動は、2026年６月より開始予定です。
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 1,
                  opacity: 0.78,
                  lineHeight: 1.7,
                }}
              >
                準備が整い次第、この画面から参加グループへ移動できるようになります。
              </Typography>

              {isAdmin && userInfo?.groupActivity2026?.groupId && (
                <Button
                  onClick={() => handleSelectGroup("2026")}
                  variant="contained"
                  sx={{ ...dialogContainedButtonSx, mt: 1.5, width: "100%" }}
                >
                  2026グループへ（事務局プレビュー）
                </Button>
              )}

              <Button
                onClick={() => setGroupSelectorOpen(false)}
                variant="contained"
                sx={{ ...dialogContainedButtonSx, mt: 1.5, width: "100%" }}
              >
                閉じる
              </Button>
            </Box>
          ) : (
            <Stack spacing={1.5} sx={{ mt: 1, mb: 1 }}>
              {userInfo?.participatingGroup && (
                <Button
                  variant="outlined"
                  onClick={() => handleSelectGroup("2024")}
                  sx={dialogOutlinedButtonSx}
                >
                  2024グループへ
                </Button>
              )}

              {userInfo?.groups2025 && (
                <Button
                  variant="outlined"
                  onClick={() => handleSelectGroup("2025")}
                  sx={dialogOutlinedButtonSx}
                >
                  2025グループへ
                </Button>
              )}

              {userInfo?.groups2025SecondHalf && (
                <Button
                  variant="contained"
                  onClick={() => handleSelectGroup("2025yae")}
                  sx={dialogContainedButtonSx}
                >
                  2025八重の誓グループへ
                </Button>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* 交流：権限なしダイアログ */}
      <Dialog
        open={noCommunityOpen}
        onClose={() => setNoCommunityOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 0 }}>
          <DialogSectionHeader
            icon={<ForumRoundedIcon />}
            label="COMMUNITY"
            subtitle="交流チャット"
            theme={theme}
          />
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 1, mb: 1, px: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              {noCommunityTitle}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.8,
                opacity: 0.78,
                lineHeight: 1.6,
              }}
            >
              {noCommunityBody}
              {!communityCfg.enabled ? null : (
                <>
                  <br />
                  必要な条件を満たすと閲覧できるようになります。
                </>
              )}
            </Typography>

            {communityCfg.enabled && (
              <Button
                variant="contained"
                onClick={() => {
                  setNoCommunityOpen(false);
                  navigate("/mm2026join-content");
                  setMobileOpen(false);
                }}
                sx={{ ...dialogContainedButtonSx, mt: 1.3, width: "100%" }}
              >
                お支払いページへ
              </Button>
            )}

            <Button
              variant="outlined"
              onClick={() => setNoCommunityOpen(false)}
              sx={{ ...dialogOutlinedButtonSx, mt: 1.1, width: "100%" }}
            >
              閉じる
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

/* ==========================================================
    🧭 左レール
   ========================================================== */
function NavRail({
  menus,
  activeKey,
  onSelect,
  onOpenThemeMenu,
  onOpenRibbonMenu,
  onOpenAccountMenu,
  avatarUrl,
  theme,
  ribbonBubble,
  groupActivityTooltip,
  promoIcon,
}) {
  const tooltipActive = !!(
    groupActivityTooltip?.enabled && groupActivityTooltip?.text
  );
  const accent = theme.railActive || "#5A35FF";
  return (
    <Box
      sx={{
        height: "100%",
        p: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box sx={{ height: 8 }} />
      <Divider sx={{ my: 1.5, width: 44 }} />

      {menus.map((m) => {
        const item = (
          <RailItem
            label={m.label}
            icon={m.icon}
            active={activeKey === m.key}
            disabled={m.disabled}
            badge={m.badge}
            badgeCount={m.badgeCount}
            onClick={() => onSelect(m.key, m.href, m.disabled)}
            theme={theme}
          />
        );
        if (m.key === "mypage" && tooltipActive) {
          return (
            <Tooltip
              key={m.key}
              open
              arrow
              placement="right"
              title={groupActivityTooltip.text}
              disableInteractive
              slotProps={{
                // ★ Tooltip は portal 経由で body 直下に描画されるため、
                //   親レール（モバイルで display:none）に従わない。
                //   popper 自体をモバイルで非表示にしないと左上に残ってしまう。
                popper: {
                  sx: { display: { xs: "none", md: "block" } },
                },
                tooltip: {
                  sx: {
                    bgcolor: accent,
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    px: 1.4,
                    py: 0.9,
                    maxWidth: 220,
                    boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                  },
                },
                arrow: { sx: { color: accent } },
              }}
            >
              <Box sx={{ width: "100%" }}>{item}</Box>
            </Tooltip>
          );
        }
        return <Box key={m.key} sx={{ width: "100%" }}>{item}</Box>;
      })}

      <Box sx={{ flexGrow: 1 }} />

      {/* ユーザーアイコン */}
      <IconButton
        onClick={(e) => onOpenAccountMenu(e.currentTarget)}
        sx={{
          mb: 1,
          width: 44,
          height: 44,
          borderRadius: "50%",
          bgcolor: theme.railItemBg,
          boxShadow: "0 8px 20px rgba(30,12,90,0.08)",
        }}
      >
        <Avatar
          src={avatarUrl || undefined}
          alt="user avatar"
          sx={{ width: 32, height: 32 }}
        />
      </IconButton>

      {/* 上部プッシュ通知アイコン */}
      {promoIcon}

      {/* リボンメニューボタン */}
      <Box sx={{ position: "relative", mb: 1 }}>
        {ribbonBubble?.enabled && (
          <Box
            sx={{
              position: "absolute",
              left: 52,
              top: "50%",
              transform: "translateY(-50%)",
              whiteSpace: "nowrap",
              bgcolor: theme.railActive ?? "#5A35FF",
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              px: 1.4,
              py: 0.6,
              borderRadius: "12px 12px 12px 4px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
              "&::before": {
                content: '""',
                position: "absolute",
                left: -6,
                top: "50%",
                transform: "translateY(-50%)",
                width: 0,
                height: 0,
                borderTop: "6px solid transparent",
                borderBottom: "6px solid transparent",
                borderRight: `6px solid ${theme.railActive ?? "#5A35FF"}`,
              },
            }}
          >
            {ribbonBubble.text}
          </Box>
        )}
        <IconButton
          onClick={onOpenRibbonMenu}
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: theme.railItemBg,
            boxShadow: "0 8px 20px rgba(30,12,90,0.08)",
          }}
        >
          <MilitaryTechRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* テーマ切り替えボタン */}
      <IconButton
        onClick={onOpenThemeMenu}
        sx={{
          mb: 1.5,
          width: 44,
          height: 44,
          borderRadius: 2,
          bgcolor: theme.railItemBg,
          boxShadow: "0 8px 20px rgba(30,12,90,0.08)",
        }}
      >
        <ColorLensRoundedIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

/* ==========================================================
    🎀 ダイアログ用セクションヘッダー
   ========================================================== */
function DialogSectionHeader({ icon, label, subtitle, theme }) {
  const accent = theme.railActive || "#5A35FF";
  const textBase = theme.railText || "rgba(30,30,60,0.9)";
  const surfaceChip = theme.railItemBg || "#FFFFFF";

  return (
    <Stack direction="row" spacing={1.4} alignItems="center">
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 2,
          bgcolor: surfaceChip,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 6px 14px rgba(20,16,40,0.12)",
        }}
      >
        {React.cloneElement(icon, {
          sx: { fontSize: 20, color: accent },
        })}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontSize: 13,
            color: accent,
          }}
        >
          {label}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.3,
              color: textBase,
              opacity: 0.78,
              whiteSpace: "nowrap",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

/* ==========================================================
    🎛 各レールアイテム
   ========================================================== */
function RailItem({ label, icon, active, disabled, badge, badgeCount, onClick, theme }) {
  const baseColor = theme.railText || "rgba(60,40,120,0.9)";
  const activeColor = theme.railActive || "#5A35FF";

  return (
    <Stack
      alignItems="center"
      spacing={0.5}
      sx={{
        width: "100%",
        py: 0.75,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.35 : 1,
        transition: "all .18s ease",
        color: active ? activeColor : baseColor,
        "&:hover": disabled
          ? {}
          : {
              transform: "translateY(-1px)",
              color: activeColor,
            },
      }}
      onClick={disabled ? undefined : onClick}
    >
      <IconButton
        disableRipple
        sx={{
          width: 52,
          height: 52,
          borderRadius: 3,
          background: active ? theme.railItemActiveBg : theme.railItemBg,
          boxShadow: active
            ? "inset 0 0 0 2px rgba(116,86,255,0.45), 0 8px 22px rgba(60,40,120,0.18)"
            : "0 8px 20px rgba(30,12,90,0.06)",
          color: active ? activeColor : baseColor,
        }}
      >
        {badge ? (
          <Badge
            color="error"
            overlap="circular"
            max={99}
            badgeContent={badgeCount > 0 ? badgeCount : undefined}
            variant={badgeCount > 0 ? "standard" : "dot"}
            sx={{
              "& .MuiBadge-badge": {
                boxShadow: "0 0 0 2px #fff",
                fontSize: 10,
                height: 16,
                minWidth: 16,
                px: 0.4,
              },
            }}
          >
            {React.cloneElement(icon, { fontSize: "medium" })}
          </Badge>
        ) : (
          React.cloneElement(icon, { fontSize: "medium" })
        )}
      </IconButton>
      <Typography
        variant="caption"
        sx={{
          fontWeight: active ? 800 : 600,
          letterSpacing: "0.03em",
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}
